// Canonical identity for Aussie Inspections.
//
// Every LocalBusiness block on the site must carry BUSINESS_ID. Without a
// shared @id a parser sees one business per page — six "Aussie Inspections"
// that happen to share a name — so no page's evidence accrues to the others
// and none of them reconciles with the Google Business Profile.
//
// Verified against the live profile on 2026-10-07:
//   place 0x2cc0913060f078e5:0x341368e1b79a6d2c   kgmid /g/11xrg8gvhg
//   5.0 from 6 reviews, category "Home inspector",
//   18, Level 1/20 Knuckey St, Darwin City NT 0800, 0413 245 519.

export const SITE_URL = "https://aussieinspections.com.au";

// The one node every LocalBusiness block shares. It must stay byte-identical
// everywhere — being identical is the entire function of it.
export const BUSINESS_ID = `${SITE_URL}/#business`;

export const BUSINESS_NAME = "Aussie Inspections";
// Legal entity, from AccountsAndGo's corporate register (read 22 Oct 2026):
// Aussie Inspections Pty Ltd, ACN 686 719 291, incorporated 1 May 2025.
export const LEGAL_NAME = "Aussie Inspections Pty Ltd";
export const ABN = "51 686 719 291";
export const BUSINESS_PHONE = "+61413245519";
export const BUSINESS_EMAIL = "info@aussieinspections.com.au";

// Google Maps CID for the verified profile.
//
// The previous value (01292979771422825808 = 0x11f19640696c5d50) resolved to
// an empty place at Google's fallback coordinates near Adelaide. It was the
// only link tying this site to the profile and it pointed at nothing.
// Rendering the URL in a browser is the only check that catches this —
// Maps is an SPA and returns HTTP 200 for any CID, valid or not.
export const GOOGLE_MAPS_URL =
  "https://www.google.com/maps?cid=3752458233187036460";

export const SAME_AS = [
  GOOGLE_MAPS_URL,
  "https://www.facebook.com/aussieinspections",
  "https://www.linkedin.com/company/aussie-inspections",
];

// Office address: Andy, 16 Oct 2026 gave "Level 1, 18-20 Knuckey St"; on 22 Oct
// 2026 he chose "18 Knuckey St", matching the ASIC registered address in
// AccountsAndGo. One source: the JSON-LD, the
// footer, /contact and llms.txt all read these constants.
export const STREET_ADDRESS = "Level 1, 18 Knuckey St";
export const POSTCODE = "0800";
export const ADDRESS_LINE = `${STREET_ADDRESS}, Darwin NT ${POSTCODE}`;
export const POSTAL_ADDRESS = {
  "@type": "PostalAddress",
  streetAddress: STREET_ADDRESS,
  addressLocality: "Darwin",
  postalCode: POSTCODE,
  addressRegion: "NT",
  addressCountry: "AU",
};

export const GEO = {
  "@type": "GeoCoordinates",
  latitude: "-12.4634",
  longitude: "130.8456",
};

export const OPENING_HOURS = [
  {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: "08:00",
    closes: "17:00",
  },
];

export const BUSINESS_IMAGE = `${SITE_URL}/assets/img/inspector-at-work.jpg`;
export const BUSINESS_LOGO = `${SITE_URL}/logo.png`;

// No aggregateRating in the schema, deliberately. The business has a real
// 5.0 from 6 Google reviews, but Google's structured-data policy only permits
// rating markup for reviews the site collected itself; emitting someone
// else's aggregate is a manual-action risk. The rating is stated as
// attributed readable text instead — which is what an LLM reads anyway.
//
// ONE place to update. `count` goes stale the moment a new review lands, and
// a wrong count is worse than none, so keep `verified` honest when you touch
// it: https://www.google.com/maps?cid=3752458233187036460
export const GOOGLE_RATING = {
  value: "5.0",
  count: 6,
  verified: "2026-10-07",
};

/**
 * A reference to the canonical business node, for use as provider/seller
 * inside another schema. Links to the full node rather than emitting a
 * second, thinner copy of the business.
 */
export const businessRef = {
  "@type": "LocalBusiness",
  "@id": BUSINESS_ID,
  name: BUSINESS_NAME,
  url: SITE_URL,
};

/**
 * The full canonical LocalBusiness node. Pass page-specific additions
 * (description, areaServed, employee, …) as `extra`; they are merged last so
 * a page can override a default where it genuinely differs.
 */
export function localBusiness(extra = {}) {
  return {
    "@type": "LocalBusiness",
    "@id": BUSINESS_ID,
    name: BUSINESS_NAME,
    legalName: LEGAL_NAME,
    taxID: ABN,
    url: SITE_URL,
    telephone: BUSINESS_PHONE,
    email: BUSINESS_EMAIL,
    image: BUSINESS_IMAGE,
    logo: BUSINESS_LOGO,
    address: POSTAL_ADDRESS,
    geo: GEO,
    openingHoursSpecification: OPENING_HOURS,
    priceRange: "$$",
    sameAs: SAME_AS,
    ...extra,
  };
}
